import { ITokenSale, ITokenSaleTableColumns } from '@entities/trades/token-sell';
import ReduceAndCopyString from '@features/reduce-and-copy-string';
import { ITableActionsConfig, TableActionBtns } from '@features/table-action-btns';
import { FilterControlBlock, FilterDateRange, FilterInput, FilterSelect, FilterSelectMulti } from '@features/table-filter';
import { getSelectOptions } from '@shared/components/Form';
import RequestStatusLabel from '@shared/components/RequestStatusLabel';

import { ApproveTokenSale } from '../components/ApproveTokenSale';
import { DownloadTokenSale } from '../components/DownloadTokenSale';
import { RejectTokenSale } from '../components/RejectTokenSale';
import { TokenSalesToolbar } from '../components/TokenSalesToolbar';

interface IProps {
  refetch: () => void;
  refreshInterval?: string;
  setRefreshInterval?: (interval: string) => void;
}

export const useTableSettings = ({ refetch, refreshInterval, setRefreshInterval }: IProps) => {
  // THead settings
  const columnTitles: ITokenSaleTableColumns = {
    id: 'Token Sale ID',
    trader_wallet_id: 'Trader Wallet ID',
    trader_id: 'Trader ID',
    wallet_address: 'Trader UPI ID',
    customer_transaction_id: 'UTR',
    external_order_id: 'External Order ID',
    amount: 'Amount',
    created_at: 'Created At',
    updated_at: 'Updated At',
    status: 'Status',
    dispute_file_url: 'Dispute File',
  };

  // filter
  const filterStatuses = ['new', 'pending', 'success', 'failed', 'disputed', 'cancelled', 'expired', 'refunded'];
  const filterDisputeOptions = [
    { id: 'true', label: 'Yes' },
    { id: 'false', label: 'No' },
  ];
  const renderFilterElement = (key: keyof ITokenSale) => {
    switch (key) {
      case 'status':
        return <FilterSelectMulti name={key} options={getSelectOptions(filterStatuses)} />;

      case 'created_at':
      case 'updated_at':
        return <FilterDateRange name={key} />;

      case 'dispute_file_url':
        return <FilterSelect name={'disputed'} options={filterDisputeOptions} />;

      default:
        return <FilterInput name={key} />;
    }
  };

  const getTheadAfter = () => (
    <th>
      <FilterControlBlock storageKey='token-sales'>
        <TokenSalesToolbar
          refetch={refetch}
          refreshInterval={refreshInterval}
          setRefreshInterval={setRefreshInterval}
        />
      </FilterControlBlock>
    </th>
  );

  // TBody settings
  const renderRow = (key: keyof ITokenSaleTableColumns, tokenSale: ITokenSale) => {
    switch (key) {
      case 'status':
        return <RequestStatusLabel status={String(tokenSale[key])} />;

      case 'customer_transaction_id':
        return (
          <ReduceAndCopyString
            navigateTo={`/transactions/deposit?filter[utr]=${tokenSale[key]}`}
            text={tokenSale[key]}
            allowedNavigateRoles={['admin', 'operator']}
          />
        );

      case 'external_order_id':
        return (
          <ReduceAndCopyString
            navigateTo={`/orders/deposit?filter[id]=${tokenSale[key]}`}
            text={tokenSale[key]}
            allowedNavigateRoles={['admin', 'operator']}
          />
        );

      case 'dispute_file_url':
        return <DownloadTokenSale tokenSale={tokenSale} />;

      default:
        return tokenSale[key];
    }
  };

  const getTbodyAfter = (tokenSale: ITokenSale) => {
    const isEnabled =
      tokenSale.status === 'disputed' || tokenSale.status === 'new' || tokenSale.status === 'pending';

    const actionBtnsConfig: ITableActionsConfig = {
      approve: {
        isEnabled,
        isVisible: true,
        popupElem: <ApproveTokenSale tokenSale={tokenSale} refetch={refetch} />,
        popupSize: 'sm',
        allowedRoles: ['admin', 'operator'],
      },
      reject: {
        isEnabled,
        isVisible: true,
        popupElem: <RejectTokenSale tokenSale={tokenSale} refetch={refetch} />,
        popupSize: 'sm',
        allowedRoles: ['admin', 'operator'],
      },
    };

    return (
      <td className='action-btns-row'>
        <TableActionBtns config={actionBtnsConfig} />
      </td>
    );
  };

  return {
    columnTitles,
    renderRow,
    renderFilterElement,
    getTheadAfter,
    getTbodyAfter,
  };
};
